// @flow
/* global jQuery */

import * as React from 'react';

import Bugsnag from 'app/lib/Bugsnag';

import Button from 'app/components/shared/Button';
import Dialog from 'app/components/shared/Dialog';
import Emojify from 'app/components/shared/Emojify';
import FormCheckboxGroup from 'app/components/shared/FormCheckboxGroup';
import FormTextarea from 'app/components/shared/FormTextarea';
import FormRadioGroup from 'app/components/shared/FormRadioGroup';
import FormSelect from 'app/components/shared/FormSelect';

type TextField = {|
  text: string,
  key: string,
  hint: ?string,
  required?: boolean,
  default?: string
|};

type SelectOption = {
  value: string,
  label: React.Node
};

type UnparsedSelectOption = string | SelectOption;

type SelectField = {|
  select: string,
  options: Array<UnparsedSelectOption>,
  key: string,
  hint: ?string,
  required?: boolean,
  multiple?: boolean,
  default?: string
|};

export type Field = TextField | SelectField;

type Props = {
  job: {
    id: string,
    activatePath: string,
    fields: Array<Field>,
    name: ?string,
    prompt: ?string,
    submit: ?string
  }
};

type State = {
  isOpen: boolean,
  submitting: boolean,
  errors: { [string]: string }
};

export default class BlockFieldsModal extends React.Component<Props, State> {
  form: ?HTMLFormElement;

  state = {
    isOpen: false,
    submitting: false,
    errors: {}
  };

  componentDidMount() {
    // FYI, These are @rails/ujs event handlers. We apply them to `document`
    // because we don't have `this.form` on mount. Each handler must check
    // for the existence of `this.form` before ultimately running!
    document.addEventListener('ajax:before', this.handleAjaxBefore);
    document.addEventListener('ajax:success', this.handleAjaxSuccess);
    document.addEventListener('ajax:error', this.handleAjaxError);
  }

  componentWillUnmount() {
    document.removeEventListener('ajax:before', this.handleAjaxBefore);
    document.removeEventListener('ajax:success', this.handleAjaxSuccess);
    document.removeEventListener('ajax:error', this.handleAjaxError);
  }

  handleAjaxBefore = (evt: Event) => {
    if (!this.form || (evt.target !== this.form)) {
      return;
    }

    const errors = {};
    const broken = [];

    // Make sure all the fields that are required have values
    for (let index = 0; index < this.props.job.fields.length; index++) {
      const field = this.props.job.fields[index];
      if (field.required) {
        if (field.multiple) {
          const el = jQuery(`[name^='job[fields][${field.key}]']:checked`);

          if (el.length < 1) {
            errors[field.key] = "This field is required";
            broken.push(jQuery(`[name^='job[fields][${field.key}]']`));
          }
        } else {
          const el = jQuery(`[name='job[fields][${field.key}]']`);

          // Get the value from the DOM
          let val = el.val().toString();

          // Get rid of whitespace for the before we validate
          val = jQuery.trim(val);

          // Add our error if the value is empty
          if (val === "") {
            errors[field.key] = "This field is required";
            broken.push(el);
          }
        }
      }
    }

    if (jQuery.isEmptyObject(errors)) {
      return this.setState({ errors: {}, submitting: true });
    }

    // Show the errors
    this.setState({ errors });

    // Focus on the first broken fields
    broken[0].focus();

    // Stop the form from submitting
    return false;
  };

  handleAjaxSuccess = (evt: Event) => {
    if (!this.form || (evt.target !== this.form)) {
      return;
    }

    this.hide();
    this.setState({ submitting: false });
  };

  handleAjaxError = (evt: Event) => {
    if (!this.form || (evt.target !== this.form)) {
      return;
    }

    this.setState({ submitting: false });
  };

  handleCloseClick = () => {
    this.hide();
  };

  show() {
    this.setState(
      { isOpen: true },
      () => {
        if (this.form) {
          // Find the first non-hidden form element, and give it focus
          const firstFormElement = this.form.querySelector('input:not([type="hidden"]),select,textarea');
          firstFormElement && firstFormElement.focus();
        }
      }
    );
  }

  hide() {
    this.setState({ isOpen: false });
  }

  render() {
    return (
      <Dialog
        isOpen={this.state.isOpen}
        onRequestClose={this.handleCloseClick}
      >
        <form
          action={this.props.job.activatePath}
          acceptCharset="UTF-8"
          method="post"
          data-remote="true"
          ref={(form) => this.form = form}
        >

          <input name="utf8" type="hidden" value="✓" />
          <input type="hidden" name={window._csrf.param} value={window._csrf.token} />

          <div className="p4">
            <h1 className="m0 h2 semi-bold mb3">
              <Emojify text={this.props.job.name || "Continue"} />
            </h1>
            {this.props.job.prompt && (
              <p><Emojify text={this.props.job.prompt} /></p>
            )}

            {this.renderFields()}

            <Button
              type="submit"
              className="col-12"
              disabled={this.state.submitting}
            >
              <Emojify
                text={this.props.job.submit || "Continue"}
              />
            </Button>
          </div>
        </form>
      </Dialog>
    );
  }

  renderFields(): Array<React.Node> {
    return this.props.job.fields.map((field, index) => {
      const id = `field-${this.props.job.id}-${index}`;
      const name = `job[fields][${field.key}]`;
      const error = this.state.errors[field.key];

      if (field.text) {
        return (
          <FormTextarea.Autosize
            name={name}
            id={id}
            key={id}
            label={<Emojify text={field.text} />}
            help={field.hint && (<Emojify text={field.hint} />)}
            errors={error && [error]}
            defaultValue={field.default}
            required={field.required}
          />
        );
      }

      if (field.select) {
        if (field.multiple) {
          return (
            <FormCheckboxGroup
              name={`${name}[]`}
              id={id}
              key={id}
              label={<Emojify text={field.select} />}
              help={field.hint && (<Emojify text={field.hint} />)}
              errors={error ? [error] : []}
              value={field.default}
              required={field.required}
              options={field.options.map((option) => ({ ...this.parseOption(option, true) }))}
            />
          );
        }

        if (field.options.length <= 6 && field.required) {
          return (
            <FormRadioGroup
              name={name}
              id={id}
              key={id}
              label={<Emojify text={field.select} />}
              help={field.hint && (<Emojify text={field.hint} />)}
              errors={error && [error]}
              value={field.default}
              required={field.required}
              options={field.options.map((option) => ({ ...this.parseOption(option, true) }))}
            />
          );
        }

        return (
          <FormSelect
            name={name}
            id={id}
            key={id}
            label={<Emojify text={field.select} />}
            help={field.hint && (<Emojify text={field.hint} />)}
            errors={error ? [error] : []}
            defaultValue={field.default}
            required={field.required}
            options={field.options.map((option) => ({ ...this.parseOption(option, false) }))}
          />
        );
      }

      Bugsnag.notify(new TypeError(`Unrecognised block field with keys: ${Object.keys(field).join(', ')}`));

      return null;
    });
  }

  parseOption(option: ?UnparsedSelectOption, emojify: boolean): SelectOption {
    if (!option) {
      return { value: "", label: "" };
    }

    if (typeof option === 'object') {
      return { ...option, label: (emojify ? <Emojify text={option.label} /> : option.label) };
    }

    return { value: option, label: (emojify ? <Emojify text={option} /> : option) };
  }
}
